import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import ReactSelect from "react-select";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const FilterWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

// react-select light styling
const selectStyles = {
  container: (base) => ({ ...base, minWidth: 240 }),
  menu: (base) => ({ ...base, zIndex: 5 }),
  control: (base) => ({
    ...base,
    borderRadius: 8,
    minHeight: 40,
    borderColor: "#ccc",
    boxShadow: "none",
    ":hover": { borderColor: "#aaa" },
    backgroundColor: "#fff",
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 8px" }),
  input: (base) => ({ ...base, margin: 0 }),
  placeholder: (base) => ({ ...base, color: "#777" }),
};

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ContractorActivityTrendZakupy = ({ data = [] }) => {
  const [contractors, setContractors] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedContractors, setSelectedContractors] = useState({
    contractor1: "",
    contractor2: "",
    contractor3: "",
  });

  const monthsPL = [
    "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
    "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"
  ];

  // Build options for selects
  const yearOptions = useMemo(
    () => years.map((y) => ({ value: String(y), label: String(y) })),
    [years]
  );
  const contractorOptions = useMemo(
    () => contractors.map((c) => ({ value: c, label: c })),
    [contractors]
  );

  const selectedYearOption = useMemo(
    () => (selectedYear ? { value: String(selectedYear), label: String(selectedYear) } : null),
    [selectedYear]
  );
  const selectedOption = (key) => {
    const v = selectedContractors[key];
    return v ? { value: v, label: v } : null;
  };

  useEffect(() => {
    if (!data.length) return;

    // Unique contractors (prefer name, fallback to NIP)
    const uniqueContractors = [
      ...new Set(
        data
          .map((row) => row.NazwaDostawcy?.trim() || row.NrDostawcy?.trim())
          .filter(Boolean)
      ),
    ];

    // Unique years (from Rok)
    const uniqueYears = [
      ...new Set(
        data
          .map((row) => parseInt(row.Rok, 10))
          .filter((y) => !Number.isNaN(y))
      ),
    ].sort((a, b) => b - a);

    setContractors(uniqueContractors);
    setYears(uniqueYears);

    if (!selectedYear && uniqueYears.length) setSelectedYear(String(uniqueYears[0]));
    setSelectedContractors((prev) => ({
      contractor1: prev.contractor1 || uniqueContractors[0] || "",
      contractor2: prev.contractor2 || uniqueContractors[1] || "",
      contractor3: prev.contractor3 || uniqueContractors[2] || "",
    }));
  }, [data, selectedYear]);

  // Prepare chart data
  const chartData = useMemo(() => {
    const picked = Object.values(selectedContractors).filter(Boolean);
    if (!picked.length || !selectedYear) return null;

    const filtered = data.filter(
      (row) => String(parseInt(row.Rok, 10)) === String(selectedYear)
    );

    const agg = filtered.reduce((acc, row) => {
      const contractor = row.NazwaDostawcy?.trim() || row.NrDostawcy?.trim();
      const monthIdx = (parseInt(row.Miesiac, 10) || 0) - 1;
      const value = (parseFloat(row.K_42) || 0) + (parseFloat(row.K_43) || 0);

      if (!contractor || monthIdx < 0 || monthIdx > 11) return acc;
      if (!picked.includes(contractor)) return acc;

      if (!acc[contractor]) acc[contractor] = Array(12).fill(0);
      acc[contractor][monthIdx] += value;
      return acc;
    }, {});

    const labels = monthsPL;
    const palette = ["#2e7d32", "#fb8c00", "#2196f3", "#8e24aa", "#00897b", "#7cb342"];

    const datasets = Object.keys(agg).map((name, i) => ({
      label: name,
      data: agg[name],
      borderColor: palette[i % palette.length],
      backgroundColor: "rgba(0,0,0,0)",
      tension: 0.35,
      pointRadius: 3,
      borderWidth: 2,
      fill: false,
    }));

    return { labels, datasets };
  }, [data, selectedContractors, selectedYear]);

  const handleYearChange = (opt) => setSelectedYear(opt?.value || "");
  const handleContractorChange = (key, opt) =>
    setSelectedContractors((prev) => ({ ...prev, [key]: opt?.value || "" }));

  return (
    <ChartWrapper>
      <ChartTitle>
        Aktywność kontrahentów w czasie
        <InfoIconWrapper data-tooltip-id="contractor-trend-z-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="contractor-trend-z-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Aktywność kontrahentów</strong><br/>
            Wykres pokazuje zmiany zakupów dla wybranych kontrahentów (miesięcznie).<br/>
            Wyszukuj w listach roku i kontrahentów.
          </>
        }
      />

      {/* Searchable filters */}
      <FilterWrapper>
        <ReactSelect
          styles={selectStyles}
          options={yearOptions}
          value={selectedYearOption}
          onChange={handleYearChange}
          isSearchable
          placeholder="Wybierz rok"
          noOptionsMessage={() => "Brak wyników"}
        />

        {["contractor1", "contractor2", "contractor3"].map((key, i) => (
          <ReactSelect
            key={key}
            styles={selectStyles}
            options={contractorOptions}
            value={selectedOption(key)}
            onChange={(opt) => handleContractorChange(key, opt)}
            isSearchable
            placeholder={`Wybierz kontrahenta (${i + 1})`}
            noOptionsMessage={() => "Brak wyników"}
            isClearable
          />
        ))}
      </FilterWrapper>

      {chartData ? (
        <Line
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { position: "bottom" },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  label: (ctx) =>
                    `${ctx.dataset.label}: ${formatNumber(ctx.raw)}`,
                },
              },
            },
            scales: {
              x: { title: { display: true, text: "Miesiące" } },
              y: {
                beginAtZero: true,
                title: { display: true, text: "Wartość zakupów" },
                ticks: { callback: (v) => formatNumber(v) },
              },
            },
          }}
        />
      ) : (
        <p style={{ textAlign: "center" }}>
          Wybierz rok i kontrahentów, aby zobaczyć dane
        </p>
      )}
    </ChartWrapper>
  );
};

export default ContractorActivityTrendZakupy;
